import React, { useCallback, useEffect } from "react";
import { PropertyChangedEvent } from "@azure/communication-calling";
import { logger } from "@mwt/web-js-telemetry";

import {
  AudioTestState,
  DeviceTesterTestState,
} from "../../../types/Device.types";
import { DeviceTesterActionType } from "../../device-tester-context-provider/Reducer.types";
import {
  useDeviceTesterDispatchContext,
  useDeviceTesterStateContext,
} from "../../device-tester-context-provider/DeviceTesterContextProvider";
import { usePlatformTypeContext } from "../../platform-type-provider/PlatformTypeProvider";
import { useServiceContext } from "../../acs-service-provider/ServiceContextProvider";
import { Paths } from "../../../types/paths";

import {
  logStartAudioTestCall,
  logCancelAudioTestCall,
  logCompletedAudioTest,
  logAudioTestCallTimeout,
} from "./ConnectedAudioTestCallActive.telemetry";
import { AudioTestActive } from "./AudioTestActive";

export const ConnectedAudioTestActive: React.FC = () => {
  const { deviceTesterState, startAudioTest } = useDeviceTesterStateContext();
  const { deviceTesterDispatch } = useDeviceTesterDispatchContext();
  const { isMobile } = usePlatformTypeContext();
  const serviceContainer = useServiceContext();
  const acsService = serviceContainer?.acsService;

  // Redirect to error page if issue creating the Call obj
  const callCreationError = useCallback(() => {
    logger.error("Issue starting audio call");
    logAudioTestCallTimeout();
    window.location.pathname = Paths.AcsCallError;
  }, []);

  const setAudioTestState = useCallback(
    (newAudioTestState: any) => {
      deviceTesterDispatch({
        type: DeviceTesterActionType.SET_AUDIO_TEST_STATE,
        audioTestState: newAudioTestState,
      });
    },
    [deviceTesterDispatch]
  );

  const setDeviceTestState = useCallback(
    (newDeviceTestState: any) => {
      deviceTesterDispatch({
        type: DeviceTesterActionType.SET_DEVICE_TEST_STATE,
        state: newDeviceTestState,
      });
    },
    [deviceTesterDispatch]
  );

  // Callback to start audio test and call echo bot process
  const startAudioTestCall = useCallback(async () => {
    setAudioTestState(AudioTestState.STARTED);
    logStartAudioTestCall();
    const call = await startAudioTest();

    if (!call) {
      callCreationError();
    } else {
      //callback for handling call events
      const callStateChanged: PropertyChangedEvent = async () => {
        if (call.state === "Disconnected") {
          call.off("stateChanged", callStateChanged);
          logCompletedAudioTest();
          setAudioTestState(AudioTestState.RESULT_CLARITY);
        }
      };

      // Attach callback handler for connected and disconnected call states
      call.on("stateChanged", callStateChanged);
    }
  }, [startAudioTest, setAudioTestState, callCreationError]);

  // Callback to cancel audio test whilst in progress
  const cancelTest = useCallback(async () => {
    logCancelAudioTestCall();
    setDeviceTestState(DeviceTesterTestState.RESULTS);
  }, [setDeviceTestState]);

  const preconfigTest = useCallback(async () => {
    await acsService?.preconfigureTestCall(
      deviceTesterState.deviceSettings.selectedMicrophone,
      deviceTesterState.deviceSettings.selectedSpeaker
    );
  }, [
    acsService,
    deviceTesterState.deviceSettings.selectedMicrophone,
    deviceTesterState.deviceSettings.selectedSpeaker,
  ]);

  useEffect(() => {
    if (deviceTesterState.audioTestState === AudioTestState.INITIATED) {
      preconfigTest();
    }
  }, [preconfigTest, deviceTesterState.audioTestState]);

  return (
    <AudioTestActive
      startAudioTestCall={startAudioTestCall}
      cancelTest={cancelTest}
      testState={deviceTesterState.audioTestState}
      isMobile={isMobile}
    />
  );
};
